import React, { useEffect, useState, Children, Component } from 'react';
import { BookOpen, Users, Trophy, PlayCircle, CheckCircle, BarChart2, MessageCircle, Star, Award, Video, Layout, Sparkles, Brain, Rocket, Lightbulb, Globe2, Volume2, FileText, Zap, Book, Bookmark, ArrowRight, Headphones, GraduationCap, Mic, PenTool, Ear, Music, Pencil, AlignLeft, Glasses, Edit, Layers, Palette, PenLine, Stethoscope, Eye, ListChecks, LucideMessageSquare, Scroll, MenuIcon, SlidersHorizontal, BookMarked, GanttChart, PieChart, BarChart, BookmarkIcon, UserCheck, Map, Compass, CalendarCheck, BookOpenCheck, Library, GraduationCap as GraduationCapIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { CountdownTimer } from './CountdownTimer';
import { CTA } from './CTA';
import { motion } from 'framer-motion';
import { FloatingElements, AnimatedAlphabet, AnimatedLearningPath, WordScramble, InteractiveQuiz, AnimatedBook, BrainActivity } from './EducationalAnimations';
// Component for the core language skills section
const LanguageSkillsSection = () => {
  const {
    language
  } = useLanguage();
  const isRTL = language === 'ar';
  const scrollToEarlyAccess = () => {
    const form = document.querySelector('#waitlist-form');
    if (form) {
      form.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const skills = [{
    id: 'listening',
    icon: <Headphones className="w-12 h-12 text-blue-500" data-id="element-1994" />,
    title: isRTL ? 'الاستماع' : 'Listening',
    description: isRTL ? 'تحسين مهارات فهم اللغة المنطوقة من خلال تمارين استماع متنوعة' : 'Improve comprehension of spoken language through diverse listening exercises',
    color: 'blue',
    animation: {
      y: [0, -10, 0],
      rotate: [0, 5, 0]
    }
  }, {
    id: 'speaking',
    icon: <Mic className="w-12 h-12 text-accent-coral-DEFAULT dark:text-accent-coral-light" data-id="element-1995" />,
    title: isRTL ? 'التحدث' : 'Speaking',
    description: isRTL ? 'تطوير مهارات المحادثة والنطق الصحيح للكلمات والعبارات' : 'Develop conversation skills and proper pronunciation of words and phrases',
    color: 'accent-coral',
    animation: {
      y: [0, -10, 0],
      rotate: [0, -5, 0]
    }
  }, {
    id: 'reading',
    icon: <div className="w-12 h-12 text-primary-500 dark:text-primary-400" data-id="element-1996" />,
    title: isRTL ? 'القراءة' : 'Reading',
    description: isRTL ? 'تعزيز مهارات القراءة والفهم من خلال نصوص تناسب مستواك' : 'Enhance reading and comprehension skills through level-appropriate texts',
    color: 'primary',
    animation: {
      y: [0, -10, 0],
      rotate: [0, 3, 0]
    }
  }, {
    id: 'writing',
    icon: <PenTool className="w-12 h-12 text-secondary-500 dark:text-secondary-400" data-id="element-1997" />,
    title: isRTL ? 'الكتابة' : 'Writing',
    description: isRTL ? 'تطوير مهارات الكتابة بأسلوب صحيح وواضح بمختلف السياقات' : 'Develop skills to write correctly and clearly in various contexts',
    color: 'secondary',
    animation: {
      y: [0, -10, 0],
      rotate: [0, -3, 0]
    }
  }];
  return <section className="relative py-20 overflow-hidden" data-id="element-1998">
      <div className="absolute inset-0 pointer-events-none" data-id="element-1999">
        {/* Animated background elements for language skills section */}
        <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] -translate-x-1/2 -translate-y-1/2 bg-blue-500/5 rounded-full blur-3xl" data-id="element-2000" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] translate-x-1/2 translate-y-1/2 bg-accent-coral-DEFAULT/5 rounded-full blur-3xl" data-id="element-2001" />
        {/* Floating language elements */}
        {['A', 'B', 'Z', '!', '?', '.', 'ا', 'ب'].map((char, index) => <motion.div key={`floating-char-${index}`} className="absolute text-4xl font-bold text-gray-200/20 dark:text-gray-700/20" style={{
        left: `${index * 15 % 80 + 10}%`,
        top: `${index * 12 % 70 + 15}%`
      }} animate={{
        y: [0, -15, 0],
        x: [0, index % 2 === 0 ? 10 : -10, 0],
        rotate: [0, index % 2 === 0 ? 10 : -10, 0]
      }} transition={{
        duration: 6 + index,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2002">
            {char}
          </motion.div>)}
      </div>
      <div className="container mx-auto px-4 relative" data-id="element-2003">
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} data-id="element-2004">
          <motion.div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-900/30 mb-6 cursor-pointer" initial={{
          scale: 0.9
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} onClick={scrollToEarlyAccess} data-id="element-2005">
            <Layers className="w-5 h-5 text-primary-500 dark:text-primary-400" data-id="element-2006" />
            <span className="text-sm font-medium text-primary-700 dark:text-primary-400" data-id="element-2007">
              {isRTL ? 'المهارات الأساسية' : 'Core Skills'}
            </span>
          </motion.div>
          <h2 className="text-4xl sm:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-400 dark:to-blue-400 mb-6 cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2008">
            {isRTL ? 'تطوير مهارات اللغة الأربعة' : 'Develop the Four Language Skills'}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2009">
            {isRTL ? 'منهجنا الشامل يركز على تطوير جميع مهارات اللغة الإنجليزية الأساسية لضمان إتقان متكامل' : 'Our comprehensive approach focuses on developing all core English language skills to ensure complete mastery'}
          </p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2010">
          {skills.map((skill, index) => <motion.div key={skill.id} className={`relative p-6 rounded-2xl bg-white/80 dark:bg-dark-200/50 backdrop-blur-lg border border-${skill.color}-100/50 dark:border-${skill.color}-900/30 hover:border-${skill.color}-300 dark:hover:border-${skill.color}-700/50 transition-all duration-300 overflow-hidden group cursor-pointer`} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2 + index * 0.1
        }} whileHover={{
          y: -5,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
        }} onClick={scrollToEarlyAccess} data-id="element-2011">
              {/* Background glow effect */}
              <div className={`absolute -right-10 -top-10 w-40 h-40 bg-${skill.color}-500/10 dark:bg-${skill.color}-500/5 rounded-full blur-3xl group-hover:bg-${skill.color}-500/20 transition-all duration-500`} data-id="element-2012"></div>
              <div className="relative z-10" data-id="element-2013">
                <motion.div className={`p-4 rounded-xl bg-${skill.color}-50 dark:bg-${skill.color}-900/30 inline-flex mb-4`} animate={skill.animation} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: index * 0.2
            }} data-id="element-2014">
                  {skill.icon}
                </motion.div>
                <h3 className={`text-2xl font-bold mb-3 text-${skill.color}-600 dark:text-${skill.color}-400`} data-id="element-2015">
                  {skill.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2016">
                  {skill.description}
                </p>
                <motion.div className={`mt-4 inline-flex items-center gap-1 text-${skill.color}-600 dark:text-${skill.color}-400 font-medium text-sm`} whileHover={{
              x: isRTL ? -5 : 5
            }} data-id="element-2017">
                  {isRTL ? 'اكتشف التمارين' : 'Discover exercises'}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} data-id="element-2018" />
                </motion.div>
              </div>
            </motion.div>)}
        </div>
        {/* Interactive language practice preview */}
        <motion.div className="mt-20 p-8 rounded-2xl bg-gradient-to-br from-white/70 to-blue-50/50 dark:from-dark-200/70 dark:to-blue-900/10 border border-blue-100/30 dark:border-blue-800/20 shadow-xl cursor-pointer" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} onClick={scrollToEarlyAccess} data-id="element-2019">
          <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-2020">
            <div className="flex-1" data-id="element-2021">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-2022">
                {isRTL ? 'تعلم تفاعلي ممتع' : 'Fun Interactive Learning'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-2023">
                {isRTL ? 'تمارين تفاعلية وألعاب تعليمية تجعل تعلم اللغة الإنجليزية ممتعًا وفعالًا في نفس الوقت' : 'Interactive exercises and educational games make learning English both fun and effective'}
              </p>
              <div className="space-y-4" data-id="element-2024">
                {[{
                icon: <Music className="w-5 h-5" data-id="element-2025" />,
                text: isRTL ? 'تمارين نطق صوتية' : 'Pronunciation exercises'
              }, {
                icon: <MessageCircle className="w-5 h-5" data-id="element-2026" />,
                text: isRTL ? 'محادثات واقعية' : 'Real-life conversations'
              }, {
                icon: <Eye className="w-5 h-5" data-id="element-2027" />,
                text: isRTL ? 'تمارين فهم بصرية' : 'Visual comprehension activities'
              }].map((item, i) => <motion.div key={i} className="flex items-center gap-3" initial={{
                opacity: 0,
                x: isRTL ? 20 : -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: 0.3 + i * 0.1
              }} data-id="element-2028">
                    <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" data-id="element-2029">
                      {item.icon}
                    </div>
                    <span className="text-gray-700 dark:text-gray-200" data-id="element-2030">
                      {item.text}
                    </span>
                  </motion.div>)}
              </div>
              <motion.button className="mt-8 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium flex items-center gap-2 shadow-lg shadow-blue-500/20" whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.98
            }} onClick={scrollToEarlyAccess} data-id="element-2031">
                <Sparkles className="w-5 h-5" data-id="element-2032" />
                {isRTL ? 'جرب الآن' : 'Try it now'}
              </motion.button>
            </div>
            <div className="flex-1 relative" data-id="element-2033">
              <motion.div className="relative z-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-200 dark:border-gray-700" animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-2034">
                <div className="relative aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg mb-4 overflow-hidden" data-id="element-2035">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-2036">
                    <motion.div className="w-16 h-16 rounded-full bg-blue-500/90 flex items-center justify-center cursor-pointer" whileHover={{
                    scale: 1.1
                  }} onClick={scrollToEarlyAccess} data-id="element-2037">
                      <PlayCircle className="w-10 h-10 text-white" data-id="element-2038" />
                    </motion.div>
                  </div>
                  {/* Animated elements inside the video preview */}
                  <motion.div className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg text-sm font-medium" animate={{
                  opacity: [0.7, 1, 0.7]
                }} transition={{
                  duration: 3,
                  repeat: Infinity
                }} data-id="element-2039">
                    {isRTL ? 'درس المحادثة' : 'Conversation Lesson'}
                  </motion.div>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2" data-id="element-2040">
                  {isRTL ? 'كيف تتحدث بثقة في المواقف اليومية' : 'How to Speak Confidently in Everyday Situations'}
                </h4>
                <div className="flex items-center justify-between" data-id="element-2041">
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2042">
                    {isRTL ? '15 دقيقة' : '15 minutes'}
                  </span>
                  <div className="flex items-center gap-1" data-id="element-2043">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" data-id="element-2044" />
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" data-id="element-2045" />
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" data-id="element-2046" />
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" data-id="element-2047" />
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" data-id="element-2048" />
                  </div>
                </div>
              </motion.div>
              {/* Decorative elements */}
              <motion.div className="absolute top-10 -right-6 w-20 h-20 bg-accent-coral-DEFAULT/20 dark:bg-accent-coral-DEFAULT/10 rounded-full blur-xl" animate={{
              scale: [1, 1.2, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-2049" />
              <motion.div className="absolute -bottom-6 -left-6 w-24 h-24 bg-primary-500/20 dark:bg-primary-500/10 rounded-full blur-xl" animate={{
              scale: [1, 1.3, 1]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} data-id="element-2050" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};
const WhySection = ({
  features
}: {
  features: any[];
}) => {
  const {
    language
  } = useLanguage();
  const isRTL = language === 'ar';
  const scrollToEarlyAccess = () => {
    const form = document.querySelector('#waitlist-form');
    if (form) {
      form.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };
  return <section className="relative py-24 overflow-hidden" data-id="element-2051">
      <div className="absolute inset-0 pointer-events-none" data-id="element-2052">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] -translate-x-1/2 -translate-y-1/2 bg-primary-500/5 rounded-full blur-3xl" data-id="element-2053" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] translate-x-1/2 translate-y-1/4 bg-secondary-500/5 rounded-full blur-3xl" data-id="element-2054" />
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-[0.4] dark:opacity-[0.1]" data-id="element-2055" />
      </div>
      <div className="container mx-auto px-4 relative" data-id="element-2056">
        <div className="max-w-4xl mx-auto" data-id="element-2057">
          <motion.div className="text-center mb-20" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-2058">
            <motion.div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-900/30 mb-6 cursor-pointer" initial={{
            scale: 0.9
          }} whileInView={{
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} onClick={scrollToEarlyAccess} data-id="element-2059">
              <Sparkles className="w-5 h-5 text-primary-500 dark:text-primary-400" data-id="element-2060" />
              <span className="text-sm font-medium text-primary-700 dark:text-primary-400" data-id="element-2061">
                {isRTL ? 'تعلم بطريقة مختلفة' : 'Learn Differently'}
              </span>
            </motion.div>
            <h2 className="text-4xl sm:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-400 dark:to-primary-500 cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2062">
              {isRTL ? 'لماذا Arabic With English؟' : 'Why Arabic With English?'}
            </h2>
          </motion.div>
          <div className="mb-24 cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2063">
            <AnimatedLearningPath data-id="element-2064" />
          </div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} data-id="element-2065">
            {features.map((feature, index) => <motion.div key={index} className="group relative p-8 rounded-2xl bg-white/80 dark:bg-dark-200/50 backdrop-blur-lg border border-neutral-200/50 dark:border-dark-100/30 hover:border-primary-200 dark:hover:border-primary-700/50 transition-all duration-300 cursor-pointer" variants={itemVariants} style={{
            textAlign: isRTL ? 'right' : 'left'
          }} whileHover={{
            y: -8
          }} whileTap={{
            scale: 0.98
          }} onClick={scrollToEarlyAccess} data-id="element-2066">
                <div className="relative z-10" data-id="element-2067">
                  <div className="flex flex-col sm:flex-row items-start gap-6" data-id="element-2068">
                    <div className="p-4 rounded-xl bg-primary-50 dark:bg-primary-900/30 transition-colors duration-300" data-id="element-2069">
                      {feature.icon}
                    </div>
                    <div data-id="element-2070">
                      <h3 className="text-xl font-semibold mb-3 text-neutral-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300" data-id="element-2071">
                        {isRTL ? feature.titleAr : feature.title}
                      </h3>
                      <p className="text-neutral-700 dark:text-neutral-300" data-id="element-2072">
                        {isRTL ? feature.descriptionAr : feature.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-primary-50/0 to-primary-50/50 dark:from-primary-900/0 dark:to-primary-900/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2073" />
              </motion.div>)}
          </motion.div>
          <div className="mt-24 space-y-16" data-id="element-2074">
            <motion.div className="p-8 rounded-2xl bg-gradient-to-br from-white/50 to-primary-50/30 dark:from-dark-200/50 dark:to-primary-900/20 border border-primary-100/20 dark:border-primary-800/20 shadow-lg cursor-pointer" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} onClick={scrollToEarlyAccess} data-id="element-2075">
              <h3 className="text-2xl font-semibold mb-8 text-center text-primary-700 dark:text-primary-400" data-id="element-2076">
                {isRTL ? 'اختبر معلوماتك' : 'Test Your Knowledge'}
              </h3>
              <InteractiveQuiz data-id="element-2077" />
            </motion.div>
            <motion.div className="p-8 rounded-2xl bg-gradient-to-br from-white/50 to-secondary-50/30 dark:from-dark-200/50 dark:to-secondary-900/20 border border-secondary-100/20 dark:border-secondary-800/20 shadow-lg cursor-pointer" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} onClick={scrollToEarlyAccess} data-id="element-2078">
              <h3 className="text-2xl font-semibold mb-8 text-center text-secondary-700 dark:text-secondary-400" data-id="element-2079">
                {isRTL ? 'تعلم الكلمات' : 'Learn Words'}
              </h3>
              <WordScramble data-id="element-2080" />
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2081">
              <motion.div className="p-8 rounded-2xl bg-gradient-to-br from-white/50 to-accent-gold-light/30 dark:from-dark-200/50 dark:to-accent-gold-DEFAULT/20 border border-accent-gold-light/20 dark:border-accent-gold-DEFAULT/20 shadow-lg cursor-pointer" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.7
            }} onClick={scrollToEarlyAccess} data-id="element-2082">
                <h3 className="text-2xl font-semibold mb-8 text-center text-accent-gold-DEFAULT dark:text-accent-gold-light" data-id="element-2083">
                  {isRTL ? 'كتابنا التفاعلي' : 'Our Interactive Book'}
                </h3>
                <AnimatedBook data-id="element-2084" />
              </motion.div>
              <motion.div className="p-8 rounded-2xl bg-gradient-to-br from-white/50 to-primary-50/30 dark:from-dark-200/50 dark:to-primary-900/20 border border-primary-100/20 dark:border-primary-800/20 shadow-lg cursor-pointer" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.7
            }} onClick={scrollToEarlyAccess} data-id="element-2085">
                <h3 className="text-2xl font-semibold mb-8 text-center text-primary-700 dark:text-primary-400" data-id="element-2086">
                  {isRTL ? 'نشاط الدماغ' : 'Brain Activity'}
                </h3>
                <p className="text-center text-neutral-600 dark:text-neutral-400 mb-8" data-id="element-2087">
                  {isRTL ? 'انقر على الدماغ لتنشيطه!' : 'Click on the brain to activate it!'}
                </p>
                <BrainActivity data-id="element-2088" />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
// Learning Progress Section - NEW
const LearningProgressSection = () => {
  const {
    language
  } = useLanguage();
  const isRTL = language === 'ar';
  const scrollToEarlyAccess = () => {
    const form = document.querySelector('#waitlist-form');
    if (form) {
      form.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative py-20 overflow-hidden" data-id="element-2089">
      <div className="absolute inset-0 pointer-events-none" data-id="element-2090">
        <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] translate-x-1/2 -translate-y-1/2 bg-primary-500/5 rounded-full blur-3xl" data-id="element-2091" />
        <div className="absolute bottom-1/3 left-1/4 w-[350px] h-[350px] -translate-x-1/2 translate-y-1/2 bg-blue-500/5 rounded-full blur-3xl" data-id="element-2092" />
      </div>
      <div className="container mx-auto px-4 relative" data-id="element-2093">
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} data-id="element-2094">
          <motion.div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-50 dark:bg-secondary-900/30 mb-6 cursor-pointer" initial={{
          scale: 0.9
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} onClick={scrollToEarlyAccess} data-id="element-2095">
            <GanttChart className="w-5 h-5 text-secondary-500 dark:text-secondary-400" data-id="element-2096" />
            <span className="text-sm font-medium text-secondary-700 dark:text-secondary-400" data-id="element-2097">
              {isRTL ? 'تقدم مستمر' : 'Continuous Progress'}
            </span>
          </motion.div>
          <h2 className="text-4xl sm:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-secondary-600 to-blue-600 dark:from-secondary-400 dark:to-blue-400 mb-6 cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2098">
            {isRTL ? 'تتبع مسار تعلمك' : 'Track Your Learning Path'}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2099">
            {isRTL ? 'راقب تقدمك وحدد أهدافك التعليمية مع أدوات تحليل ذكية تساعدك على التحسن المستمر' : 'Monitor your progress and set learning goals with smart analytics tools that help you continuously improve'}
          </p>
        </motion.div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-id="element-2100">
          <div className="order-2 lg:order-1" data-id="element-2101">
            <div className="space-y-8" data-id="element-2102">
              {[{
              icon: <ListChecks className="w-6 h-6 text-blue-500" data-id="element-2103" />,
              title: isRTL ? 'أهداف قابلة للتحقيق' : 'Achievable Goals',
              description: isRTL ? 'حدد أهدافًا واقعية وتابع تقدمك نحو تحقيقها' : 'Set realistic goals and track your progress towards achieving them'
            }, {
              icon: <PieChart className="w-6 h-6 text-purple-500" data-id="element-2104" />,
              title: isRTL ? 'إحصائيات مفصلة' : 'Detailed Statistics',
              description: isRTL ? 'احصل على تحليل مفصل لأداءك في كل مهارة من مهارات اللغة' : 'Get detailed analysis of your performance in each language skill'
            }, {
              icon: <CalendarCheck className="w-6 h-6 text-green-500" data-id="element-2105" />,
              title: isRTL ? 'متابعة يومية' : 'Daily Tracking',
              description: isRTL ? 'تابع نشاطك اليومي واحتفظ بسجل مستمر لتعلمك' : 'Track your daily activity and maintain a continuous record of your learning'
            }, {
              icon: <Compass className="w-6 h-6 text-accent-gold-DEFAULT" data-id="element-2106" />,
              title: isRTL ? 'توجيه مخصص' : 'Personalized Guidance',
              description: isRTL ? 'احصل على توصيات مخصصة بناءً على نقاط قوتك وضعفك' : 'Receive personalized recommendations based on your strengths and weaknesses'
            }].map((item, index) => <motion.div key={index} className="flex gap-5 cursor-pointer" initial={{
              opacity: 0,
              x: isRTL ? 50 : -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2 + index * 0.1
            }} onClick={scrollToEarlyAccess} data-id="element-2107">
                  <div className="p-3 rounded-xl bg-white dark:bg-dark-200 shadow-md" data-id="element-2108">
                    {item.icon}
                  </div>
                  <div data-id="element-2109">
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white" data-id="element-2110">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300" data-id="element-2111">
                      {item.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
          <motion.div className="order-1 lg:order-2 cursor-pointer" initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} onClick={scrollToEarlyAccess} data-id="element-2112">
            <div className="relative" data-id="element-2113">
              <div className="relative bg-white dark:bg-dark-200 rounded-2xl shadow-xl p-6 border border-gray-200 dark:border-gray-700" data-id="element-2114">
                <div className="flex justify-between items-center mb-6" data-id="element-2115">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2116">
                    {isRTL ? 'لوحة تقدم التعلم' : 'Learning Progress Dashboard'}
                  </h3>
                  <div className="flex gap-2" data-id="element-2117">
                    <button className="p-2 rounded-lg bg-gray-100 dark:bg-dark-300 text-gray-500 dark:text-gray-400" onClick={scrollToEarlyAccess} data-id="element-2118">
                      <BarChart className="w-5 h-5" data-id="element-2119" />
                    </button>
                    <button className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-500 dark:text-primary-400" onClick={scrollToEarlyAccess} data-id="element-2120">
                      <SlidersHorizontal className="w-5 h-5" data-id="element-2121" />
                    </button>
                  </div>
                </div>
                <div className="mb-8" data-id="element-2122">
                  <div className="flex justify-between items-end mb-2" data-id="element-2123">
                    <div data-id="element-2124">
                      <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2125">
                        {isRTL ? 'المستوى الحالي' : 'Current Level'}
                      </span>
                      <h4 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2126">
                        {isRTL ? 'المتوسط المتقدم' : 'Upper Intermediate'}
                      </h4>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-full" data-id="element-2127">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400" data-id="element-2128">
                        B2
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-3 bg-gray-100 dark:bg-dark-300 rounded-full overflow-hidden" data-id="element-2129">
                    <motion.div className="h-full bg-gradient-to-r from-blue-500 to-primary-500" style={{
                    width: '75%'
                  }} initial={{
                    width: '0%'
                  }} whileInView={{
                    width: '75%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1.5,
                    delay: 0.5
                  }} data-id="element-2130"></motion.div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-2131">
                    <span data-id="element-2132">B1</span>
                    <span data-id="element-2133">B2</span>
                    <span data-id="element-2134">C1</span>
                  </div>
                </div>
                <div className="space-y-5" data-id="element-2135">
                  {[{
                  name: isRTL ? 'الاستماع' : 'Listening',
                  progress: 82,
                  color: 'bg-blue-500'
                }, {
                  name: isRTL ? 'التحدث' : 'Speaking',
                  progress: 68,
                  color: 'bg-accent-coral-DEFAULT'
                }, {
                  name: isRTL ? 'القراءة' : 'Reading',
                  progress: 91,
                  color: 'bg-primary-500'
                }, {
                  name: isRTL ? 'الكتابة' : 'Writing',
                  progress: 63,
                  color: 'bg-secondary-500'
                }].map((skill, index) => <div key={index} className="space-y-2" data-id="element-2136">
                      <div className="flex justify-between" data-id="element-2137">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2138">
                          {skill.name}
                        </span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2139">
                          {skill.progress}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 dark:bg-dark-300 rounded-full overflow-hidden" data-id="element-2140">
                        <motion.div className={`h-full ${skill.color}`} style={{
                      width: `${skill.progress}%`
                    }} initial={{
                      width: '0%'
                    }} whileInView={{
                      width: `${skill.progress}%`
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      delay: 0.7 + index * 0.1
                    }} data-id="element-2141"></motion.div>
                      </div>
                    </div>)}
                </div>
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700" data-id="element-2142">
                  <div className="flex justify-between items-center" data-id="element-2143">
                    <div data-id="element-2144">
                      <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2145">
                        {isRTL ? 'الهدف القادم' : 'Next Goal'}
                      </span>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-2146">
                        {isRTL ? 'المتقدم' : 'Advanced (C1)'}
                      </h4>
                    </div>
                    <motion.button className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium" whileHover={{
                    scale: 1.03
                  }} whileTap={{
                    scale: 0.98
                  }} onClick={scrollToEarlyAccess} data-id="element-2147">
                      <BookOpenCheck className="w-4 h-4" data-id="element-2148" />
                      {isRTL ? 'عرض خطة التعلم' : 'View Learning Plan'}
                    </motion.button>
                  </div>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-blue-500/10 rounded-full blur-xl -z-10" data-id="element-2149" />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary-500/10 rounded-full blur-xl -z-10" data-id="element-2150" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
// Study Plan Section - NEW
const StudyPlanSection = () => {
  const {
    language
  } = useLanguage();
  const isRTL = language === 'ar';
  const scrollToEarlyAccess = () => {
    const form = document.querySelector('#waitlist-form');
    if (form) {
      form.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const weekdays = isRTL ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'] : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return <section className="relative py-12 sm:py-16 md:py-20 overflow-hidden bg-gradient-to-br from-white to-blue-50 dark:from-dark-200 dark:to-dark-300" data-id="element-2151">
      <div className="container mx-auto px-4 relative" data-id="element-2152">
        <motion.div className="text-center mb-8 sm:mb-12 md:mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} data-id="element-2153">
          <motion.div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-blue-50 dark:bg-blue-900/30 mb-4 sm:mb-6 cursor-pointer" initial={{
          scale: 0.9
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} onClick={scrollToEarlyAccess} data-id="element-2154">
            <CalendarCheck className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 dark:text-blue-400" data-id="element-2155" />
            <span className="text-xs sm:text-sm font-medium text-blue-700 dark:text-blue-400" data-id="element-2156">
              {isRTL ? 'خطة دراسية منظمة' : 'Organized Study Plan'}
            </span>
          </motion.div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-6 cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2157">
            {isRTL ? 'خطة أسبوعية مخصصة' : 'Personalized Weekly Plan'}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2158">
            {isRTL ? 'جدول دراسي مخصص يساعدك على تنظيم وقتك وتحقيق أقصى استفادة من دراستك' : 'A customized study schedule that helps you organize your time and get the most out of your studies'}
          </p>
        </motion.div>
        <motion.div className="bg-white dark:bg-dark-200 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden cursor-pointer" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} onClick={scrollToEarlyAccess} data-id="element-2159">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2160">
            <div data-id="element-2161">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white" data-id="element-2162">
                {isRTL ? 'خطة الدراسة الأسبوعية' : 'Weekly Study Plan'}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400" data-id="element-2163">
                {isRTL ? 'مخصصة لمستوى B2 (المتوسط المتقدم)' : 'Customized for B2 Level (Upper Intermediate)'}
              </p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto justify-end" data-id="element-2164">
              <button className="px-2 sm:px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-dark-300 dark:hover:bg-dark-400 text-gray-700 dark:text-gray-300 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1" onClick={scrollToEarlyAccess} data-id="element-2165">
                <BookmarkIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2166" />
                <span className="hidden xs:inline" data-id="element-2167">
                  {isRTL ? 'حفظ' : 'Save'}
                </span>
              </button>
              <button className="px-2 sm:px-3 py-1.5 bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/30 dark:hover:bg-primary-900/50 text-primary-600 dark:text-primary-400 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1" onClick={scrollToEarlyAccess} data-id="element-2168">
                <UserCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2169" />
                <span className="hidden xs:inline" data-id="element-2170">
                  {isRTL ? 'تخصيص' : 'Customize'}
                </span>
              </button>
            </div>
          </div>
          {/* Calendar grid - responsive version */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 divide-y sm:divide-y md:divide-y-0 md:divide-x rtl:md:divide-x-reverse divide-gray-200 dark:divide-gray-700" data-id="element-2171">
            {weekdays.map((day, index) => <motion.div key={day} className="p-3 sm:p-4 md:p-6 cursor-pointer" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1 + index * 0.05
          }} onClick={scrollToEarlyAccess} data-id="element-2172">
                <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3" data-id="element-2173">
                  {day}
                </h4>
                {index === 5 || index === 6 ? <div className="flex items-center justify-center h-28 sm:h-32 md:h-40 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-2174">
                    <div className="text-center" data-id="element-2175">
                      <div className="p-2 sm:p-3 bg-gray-50 dark:bg-dark-300 rounded-full inline-flex mb-2" data-id="element-2176">
                        <BookMarked className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 dark:text-gray-500" data-id="element-2177" />
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400" data-id="element-2178">
                        {isRTL ? 'يوم راحة' : 'Rest Day'}
                      </p>
                    </div>
                  </div> : <div className="space-y-2 sm:space-y-3" data-id="element-2179">
                    {[{
                time: index % 3 === 0 ? '09:00 AM' : index % 3 === 1 ? '10:30 AM' : '02:00 PM',
                title: index % 4 === 0 ? isRTL ? 'تمارين استماع' : 'Listening Practice' : index % 4 === 1 ? isRTL ? 'قواعد اللغة' : 'Grammar Rules' : index % 4 === 2 ? isRTL ? 'مهارات المحادثة' : 'Speaking Skills' : isRTL ? 'تمارين كتابة' : 'Writing Exercise',
                duration: '45 min',
                icon: index % 4 === 0 ? <Headphones className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2180" /> : index % 4 === 1 ? <AlignLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2181" /> : index % 4 === 2 ? <Mic className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2182" /> : <PenTool className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2183" />,
                color: index % 4 === 0 ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' : index % 4 === 1 ? 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400' : index % 4 === 2 ? 'bg-accent-coral-DEFAULT/10 text-accent-coral-DEFAULT dark:bg-accent-coral-DEFAULT/20 dark:text-accent-coral-light' : 'bg-secondary-50 text-secondary-600 dark:bg-secondary-900/20 dark:text-secondary-400'
              }, {
                time: index % 3 === 0 ? '11:00 AM' : index % 3 === 1 ? '03:30 PM' : '05:00 PM',
                title: index % 4 === 0 ? isRTL ? 'قراءة نصوص' : 'Text Reading' : index % 4 === 1 ? isRTL ? 'مفردات جديدة' : 'New Vocabulary' : index % 4 === 2 ? isRTL ? 'تمارين نطق' : 'Pronunciation Drills' : isRTL ? 'محادثة مع معلم' : 'Teacher Conversation',
                duration: '30 min',
                icon: index % 4 === 0 ? <div className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2184" /> : index % 4 === 1 ? <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2185" /> : index % 4 === 2 ? <Music className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2186" /> : <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2187" />,
                color: index % 4 === 0 ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400' : index % 4 === 1 ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400' : index % 4 === 2 ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400' : 'bg-accent-gold-light/30 text-accent-gold-DEFAULT dark:bg-accent-gold-DEFAULT/20 dark:text-accent-gold-light'
              }].map((session, sessionIndex) => <div key={sessionIndex} className="p-2 sm:p-3 rounded-lg border border-gray-100 dark:border-gray-800 bg-white dark:bg-dark-300 shadow-sm hover:shadow-md transition-shadow duration-300 study-session-card" data-id="element-2188">
                        <div className="flex justify-between items-start mb-1 sm:mb-2" data-id="element-2189">
                          <div className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-xs font-medium ${session.color}`} data-id="element-2190">
                            {session.time}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2191">
                            {session.duration}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2" data-id="element-2192">
                          {session.icon}
                          <span className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-1" data-id="element-2193">
                            {session.title}
                          </span>
                        </div>
                      </div>)}
                  </div>}
              </motion.div>)}
          </div>
          <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center flex-wrap gap-4" data-id="element-2194">
            <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-2195">
              <div className="p-2 rounded-full bg-blue-50 dark:bg-blue-900/20" data-id="element-2196">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" data-id="element-2197" />
              </div>
              <div data-id="element-2198">
                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400" data-id="element-2199">
                  {isRTL ? 'إجمالي وقت الدراسة' : 'Total Study Time'}
                </div>
                <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white" data-id="element-2200">
                  7.5 {isRTL ? 'ساعات' : 'Hours'} / {isRTL ? 'أسبوع' : 'Week'}
                </div>
              </div>
            </div>
            <motion.button className="w-full sm:w-auto px-4 sm:px-5 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 touch-target" whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} onClick={scrollToEarlyAccess} data-id="element-2201">
              <CalendarCheck className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-2202" />
              {isRTL ? 'إضافة إلى التقويم' : 'Add to Calendar'}
            </motion.button>
          </div>
        </motion.div>
        <div className="mt-8 sm:mt-10 md:mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6" data-id="element-2203">
          {[{
          icon: <Library className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" data-id="element-2204" />,
          title: isRTL ? 'محتوى تعليمي منظم' : 'Organized Learning Content',
          description: isRTL ? 'دروس مرتبة بشكل متسلسل لضمان تقدم تعليمي سلس' : 'Sequentially arranged lessons to ensure smooth educational progress'
        }, {
          icon: <Map className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" data-id="element-2205" />,
          title: isRTL ? 'خرائط تعلم واضحة' : 'Clear Learning Maps',
          description: isRTL ? 'خرائط طريق تعليمية توضح مسارك من البداية وحتى الإتقان' : 'Educational roadmaps that show your path from beginning to mastery'
        }, {
          icon: <GraduationCapIcon className="w-5 h-5 sm:w-6 sm:h-6 text-accent-gold-DEFAULT" data-id="element-2206" />,
          title: isRTL ? 'شهادات إتمام المراحل' : 'Stage Completion Certificates',
          description: isRTL ? 'احصل على شهادات عند إكمال كل مرحلة من مراحل التعلم' : 'Receive certificates upon completing each learning stage'
        }].map((item, index) => <motion.div key={index} className="bg-white/80 dark:bg-dark-200/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/30 hover:border-blue-200 dark:hover:border-blue-700/50 transition-all duration-300 cursor-pointer" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3 + index * 0.1
        }} whileHover={{
          y: -5,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
        }} onClick={scrollToEarlyAccess} data-id="element-2207">
              <div className="p-2 sm:p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 inline-flex mb-3 sm:mb-4" data-id="element-2208">
                {item.icon}
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3 text-gray-900 dark:text-white" data-id="element-2209">
                {item.title}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300" data-id="element-2210">
                {item.description}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// Learning Activities Section
const LearningActivitiesSection = () => {
  const {
    language
  } = useLanguage();
  const isRTL = language === 'ar';
  const scrollToEarlyAccess = () => {
    const form = document.querySelector('#waitlist-form');
    if (form) {
      form.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative py-20 overflow-hidden" data-id="element-2211">
      <div className="container mx-auto px-4 relative" data-id="element-2212">
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} data-id="element-2213">
          <motion.div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-coral-DEFAULT/10 dark:bg-accent-coral-DEFAULT/20 mb-6 cursor-pointer" initial={{
          scale: 0.9
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} onClick={scrollToEarlyAccess} data-id="element-2214">
            <Layout className="w-5 h-5 text-accent-coral-DEFAULT dark:text-accent-coral-light" data-id="element-2215" />
            <span className="text-sm font-medium text-accent-coral-DEFAULT dark:text-accent-coral-light" data-id="element-2216">
              {isRTL ? 'أنشطة تفاعلية' : 'Interactive Activities'}
            </span>
          </motion.div>
          <h2 className="text-4xl sm:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-accent-coral-DEFAULT to-primary-600 dark:from-accent-coral-light dark:to-primary-400 mb-6 cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2217">
            {isRTL ? 'تعلم من خلال الممارسة' : 'Learn Through Practice'}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2218">
            {isRTL ? 'مجموعة متنوعة من الأنشطة التفاعلية لتعزيز مهاراتك في اللغة الإنجليزية' : 'A variety of interactive activities to enhance your English language skills'}
          </p>
        </motion.div>
      </div>
    </section>;
};
export const ArabicWithEnglishLanding = () => {
  const {
    t,
    language,
    toggleLanguage
  } = useLanguage();
  const isRTL = language === 'ar';
  // Word of the day data
  const [wordOfDay, setWordOfDay] = useState({
    english: 'Apple',
    arabic: 'تفاحة',
    usage: 'I eat an apple every day.',
    usageAr: 'آكل تفاحة كل يوم.',
    pronunciation: '/ˈæp.əl/'
  });
  // Global function to scroll to the early access form
  const scrollToEarlyAccess = () => {
    const form = document.querySelector('#waitlist-form');
    if (form) {
      form.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  // Learning path steps
  const learningSteps = [{
    id: 'vocabulary',
    icon: <Volume2 className="w-6 h-6 text-blue-500" data-id="element-2219" />,
    title: isRTL ? 'المفردات' : 'Vocabulary',
    example: isRTL ? 'مرحبًا = Hello' : 'Hello = مرحبًا'
  }, {
    id: 'grammar',
    icon: <FileText className="w-6 h-6 text-green-500" data-id="element-2220" />,
    title: isRTL ? 'القواعد' : 'Grammar',
    example: isRTL ? 'أنا = I am' : 'I am = أنا'
  }, {
    id: 'conversation',
    icon: <MessageCircle className="w-6 h-6 text-purple-500" data-id="element-2221" />,
    title: isRTL ? 'المحادثة' : 'Conversation',
    example: isRTL ? 'كيف حالك؟' : 'How are you?'
  }, {
    id: 'mastery',
    icon: <Star className="w-6 h-6 text-amber-500" data-id="element-2222" />,
    title: isRTL ? 'الإتقان' : 'Mastery',
    example: isRTL ? 'طلاقة كاملة' : 'Full Fluency'
  }];
  const features = [{
    icon: <Video className="w-6 h-6 text-primary-500 dark:text-primary-400" data-id="element-2223" />,
    title: 'Video Lessons',
    titleAr: 'دروس فيديو تفاعلية',
    description: 'Learn through short videos explained in clear Arabic',
    descriptionAr: 'تعلّم من خلال فيديوهات قصيرة مشروحة باللغة العربية الفصيحة'
  }, {
    icon: <Users className="w-6 h-6 text-secondary-500 dark:text-secondary-400" data-id="element-2224" />,
    title: 'Native Teachers',
    titleAr: 'مدرّسون ناطقون باللغة',
    description: 'Professional teachers whose native language is English!',
    descriptionAr: 'مدرّسون محترفون لغتهم الأم هي الإنجليزية!'
  }, {
    icon: <BookOpen className="w-6 h-6 text-accent-coral-DEFAULT dark:text-accent-coral-light" data-id="element-2225" />,
    title: 'Structured Curriculum',
    titleAr: 'منهج تعليمي منظم',
    description: 'From basics to proficiency, step by step',
    descriptionAr: 'من الأساسيات حتى الاحتراف، خطوة بخطوة'
  }, {
    icon: <Award className="w-6 h-6 text-accent-amber-DEFAULT dark:text-accent-amber-light" data-id="element-2226" />,
    title: 'Achievement Certificates',
    titleAr: 'شهادات إنجاز معتمدة',
    description: 'Get a certified certificate upon completion of each level',
    descriptionAr: 'احصل على شهادة معتمدة عند إنهاء كل مستوى'
  }, {
    icon: <MessageCircle className="w-6 h-6 text-accent-emerald-DEFAULT dark:text-accent-emerald-light" data-id="element-2227" />,
    title: 'Supportive Community',
    titleAr: 'مجتمع داعم للمتعلمين',
    description: 'Interact with students like you and practice with them on the platform',
    descriptionAr: 'تفاعل مع طلاب مثلك وتدرّب معهم داخل المنصة'
  }, {
    icon: <BarChart2 className="w-6 h-6 text-accent-purple-DEFAULT dark:text-accent-purple-light" data-id="element-2228" />,
    title: 'Track Progress',
    titleAr: 'تتبع تقدمك التعليمي',
    description: 'Smart dashboard showing your level and how much left to reach your goal!',
    descriptionAr: 'لوحة تحكم ذكية تعرض مستواك، وكم تبقى لتحقيق هدفك!'
  }];
  const benefits = [{
    icon: <GraduationCap className="w-5 h-5 text-primary-500 dark:text-primary-400" data-id="element-2229" />,
    title: isRTL ? 'تعلم سريع' : 'Fast Learning',
    description: isRTL ? 'منهج مركز لتعلم أسرع وأكثر فعالية' : 'Focused curriculum for faster, more effective learning'
  }, {
    icon: <Headphones className="w-5 h-5 text-secondary-500 dark:text-secondary-400" data-id="element-2230" />,
    title: isRTL ? 'دعم مستمر' : 'Continuous Support',
    description: isRTL ? 'مساعدة على مدار الساعة طوال أيام الأسبوع' : '24/7 help when you need it'
  }, {
    icon: <Trophy className="w-5 h-5 text-accent-gold-DEFAULT dark:text-accent-gold-light" data-id="element-2231" />,
    title: isRTL ? 'تدريبات عملية' : 'Practical Exercises',
    description: isRTL ? 'تطبيق مباشر للمهارات في مواقف حقيقية' : 'Apply skills in real-life situations'
  }];
  // Mobile navigation state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  return <div className="min-h-screen w-full bg-gradient-to-br from-light-100 via-light-200 to-light-300 dark:from-dark-100 dark:via-dark-200 dark:to-dark-300 text-neutral-900 dark:text-white transition-colors duration-300 overflow-x-hidden" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-2232">
      {/* Mobile Navigation Menu */}
      <div className="md:hidden fixed top-4 right-4 z-50" data-id="element-2233">
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg bg-white/80 dark:bg-dark-200/80 backdrop-blur-sm shadow-lg border border-gray-200/50 dark:border-gray-700/50" data-id="element-2234">
          <MenuIcon className="w-6 h-6 text-gray-700 dark:text-gray-300" data-id="element-2235" />
        </button>
      </div>
      {mobileMenuOpen && <motion.div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={() => setMobileMenuOpen(false)} data-id="element-2236">
          <motion.div className="absolute top-0 right-0 w-64 h-full bg-white dark:bg-dark-200 shadow-xl" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} onClick={e => e.stopPropagation()} data-id="element-2237">
            <div className="p-6" data-id="element-2238">
              <div className="flex justify-between items-center mb-8" data-id="element-2239">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2240">
                  {isRTL ? 'القائمة' : 'Menu'}
                </h3>
                <button onClick={() => setMobileMenuOpen(false)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-300" data-id="element-2241">
                  <X className="w-5 h-5 text-gray-700 dark:text-gray-300" data-id="element-2242" />
                </button>
              </div>
              <div className="space-y-6" data-id="element-2243">
                <button onClick={() => {
              toggleLanguage();
              scrollToEarlyAccess();
            }} className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-300 text-left" data-id="element-2244">
                  <Globe2 className="w-5 h-5" data-id="element-2245" />
                  <span className="text-gray-700 dark:text-gray-300" data-id="element-2246">
                    {language === 'en' ? 'العربية' : 'English'}
                  </span>
                </button>
                {[{
              icon: <Home className="w-5 h-5" data-id="element-2247" />,
              label: isRTL ? 'الرئيسية' : 'Home'
            }, {
              icon: <BookOpen className="w-5 h-5" data-id="element-2248" />,
              label: isRTL ? 'الدورات' : 'Courses'
            }, {
              icon: <Users className="w-5 h-5" data-id="element-2249" />,
              label: isRTL ? 'المعلمون' : 'Teachers'
            }, {
              icon: <MessageCircle className="w-5 h-5" data-id="element-2250" />,
              label: isRTL ? 'المجتمع' : 'Community'
            }, {
              icon: <HelpCircle className="w-5 h-5" data-id="element-2251" />,
              label: isRTL ? 'المساعدة' : 'Help'
            }].map((item, index) => <button key={index} className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-300 text-left" onClick={scrollToEarlyAccess} data-id="element-2252">
                    <span className="text-primary-500 dark:text-primary-400" data-id="element-2253">
                      {item.icon}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-2254">
                      {item.label}
                    </span>
                  </button>)}
              </div>
              <div className="mt-12 space-y-4" data-id="element-2255">
                <button className="w-full py-2 px-4 bg-gray-100 dark:bg-dark-300 text-gray-700 dark:text-gray-300 rounded-lg font-medium" onClick={scrollToEarlyAccess} data-id="element-2256">
                  {isRTL ? 'تسجيل الدخول' : 'Log In'}
                </button>
                <button className="w-full py-2 px-4 bg-primary-600 text-white rounded-lg font-medium" onClick={scrollToEarlyAccess} data-id="element-2257">
                  {isRTL ? 'إنشاء حساب' : 'Sign Up'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>}
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden w-full" data-id="element-2258">
        {/* Enhanced animated background elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-2259">
          {/* Educational icons floating in background */}
          {[{
          icon: <BookOpen className="w-full h-full" data-id="element-2260" />,
          color: 'text-blue-500/10 dark:text-blue-400/5'
        }, {
          icon: <Headphones className="w-full h-full" data-id="element-2261" />,
          color: 'text-primary-500/10 dark:text-primary-400/5'
        }, {
          icon: <PenTool className="w-full h-full" data-id="element-2262" />,
          color: 'text-secondary-500/10 dark:text-secondary-400/5'
        }, {
          icon: <MessageCircle className="w-full h-full" data-id="element-2263" />,
          color: 'text-accent-gold-DEFAULT/10 dark:text-accent-gold-light/5'
        }, {
          icon: <Mic className="w-full h-full" data-id="element-2264" />,
          color: 'text-accent-coral-DEFAULT/10 dark:text-accent-coral-light/5'
        }, {
          icon: <GraduationCap className="w-full h-full" data-id="element-2265" />,
          color: 'text-green-500/10 dark:text-green-400/5'
        }].map((item, index) => <motion.div key={`icon-${index}`} className={`absolute w-16 h-16 ${item.color} cursor-pointer`} style={{
          left: `${index * 19 % 80 + 10}%`,
          top: `${index * 23 % 70 + 15}%`
        }} animate={{
          y: [0, -15, 0],
          x: [0, index % 2 === 0 ? 10 : -10, 0],
          rotate: [0, index % 2 === 0 ? 10 : -10, 0]
        }} transition={{
          duration: 7 + index,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: index * 0.3
        }} onClick={scrollToEarlyAccess} data-id="element-2266">
              {item.icon}
            </motion.div>)}
          {/* Floating alphabet characters */}
          <div className="alphabet-grid" data-id="element-2267">
            {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'ا', 'ب', 'ت', 'ث', 'ج', 'ح', 'خ', 'د'].map((letter, index) => <motion.div key={`letter-${index}`} className="absolute text-4xl font-bold text-primary-500/10 dark:text-primary-400/5 cursor-pointer" style={{
            left: `${index * 17 % 85 + 5}%`,
            top: `${index * 19 % 85 + 5}%`
          }} animate={{
            y: [0, -15, 0],
            rotate: [0, index % 2 === 0 ? 5 : -5, 0],
            opacity: [0.05, 0.1, 0.05]
          }} transition={{
            duration: 5 + index % 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: index * 0.2
          }} onClick={scrollToEarlyAccess} data-id="element-2268">
                {letter}
              </motion.div>)}
          </div>
          {/* Enhanced animated gradient orbs */}
          <motion.div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-r from-primary-500/10 to-blue-500/5 blur-3xl" animate={{
          scale: [1, 1.1, 1],
          x: [0, 20, 0],
          y: [0, -20, 0]
        }} transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2269" />
          <motion.div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-l from-secondary-500/10 to-accent-gold-DEFAULT/5 blur-3xl" animate={{
          scale: [1, 1.15, 1],
          x: [0, -20, 0],
          y: [0, 20, 0]
        }} transition={{
          duration: 18,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2
        }} data-id="element-2270" />
          {/* Educational patterns */}
          <motion.div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b91f2' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }} animate={{
          backgroundPosition: ['0px 0px', '60px 60px']
        }} transition={{
          duration: 30,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-2271" />
          {/* Animated dot grid pattern */}
          <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-[0.3] dark:opacity-[0.1]" data-id="element-2272">
            <motion.div className="absolute inset-0" animate={{
            x: [0, 10, 0],
            y: [0, 5, 0]
          }} transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2273" />
          </div>
        </div>
        {/* Alphabet row at bottom - enhanced */}
        <div className="absolute bottom-5 left-0 right-0 flex justify-center z-10 overflow-hidden" data-id="element-2274">
          <motion.div className="flex items-center justify-center gap-1 sm:gap-3 px-4 py-2 bg-white/30 dark:bg-dark-200/30 backdrop-blur-md rounded-full cursor-pointer" initial={{
          y: 50,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.7,
          delay: 1
        }} onClick={scrollToEarlyAccess} data-id="element-2275">
            {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((letter, index) => <motion.div key={letter} className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full ${index === 1 ? 'bg-primary-500 text-white' : 'bg-white/80 dark:bg-dark-300/80 text-gray-800 dark:text-white'} font-bold text-lg cursor-pointer`} whileHover={{
            scale: 1.1,
            boxShadow: '0 0 12px rgba(59, 130, 246, 0.5)'
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} onClick={scrollToEarlyAccess} data-id="element-2276">
                {letter}
              </motion.div>)}
          </motion.div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-2277">
          <div className="max-w-6xl mx-auto" data-id="element-2278">
            {/* Language toggle in hero section */}
            <div className="absolute top-0 right-0 sm:right-6 md:right-8 z-10" data-id="element-2279">
              <button onClick={() => {
              toggleLanguage();
              scrollToEarlyAccess();
            }} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 dark:bg-dark-200/50 backdrop-blur-sm border border-gray-200/50 dark:border-dark-100/30 text-sm font-medium text-primary-600 dark:text-primary-400 shadow-sm hover:shadow-md transition-all duration-300" data-id="element-2280">
                <Globe2 className="w-4 h-4" data-id="element-2281" />
                {language === 'en' ? 'العربية' : 'English'}
              </button>
            </div>
            {/* Logo and title section - enhanced */}
            <motion.div className="text-center mb-12" initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7
          }} data-id="element-2282">
              <div className="w-40 h-40 mx-auto mb-6 relative cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2283">
                <motion.div className="absolute inset-0 rounded-full bg-blue-100 dark:bg-blue-900/30" animate={{
                scale: [1, 1.05, 1]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-2284"></motion.div>
                <motion.div className="absolute inset-0 flex items-center justify-center" initial={{
                rotateY: 0
              }} animate={{
                rotateY: 360
              }} transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-2285">
                  <Book className="w-20 h-20 text-primary-500" data-id="element-2286" />
                </motion.div>
                {/* Floating educational icons around the logo */}
                {[{
                icon: <Headphones className="w-6 h-6 text-blue-500" data-id="element-2287" />,
                position: 'top-0 -right-4'
              }, {
                icon: <Mic className="w-6 h-6 text-accent-coral-DEFAULT" data-id="element-2288" />,
                position: 'top-0 -left-4'
              }, {
                icon: <PenTool className="w-6 h-6 text-secondary-500" data-id="element-2289" />,
                position: 'bottom-0 -right-4'
              }, {
                icon: <div className="w-6 h-6 text-primary-500" data-id="element-2290" />,
                position: 'bottom-0 -left-4'
              }].map((item, index) => <motion.div key={`floating-icon-${index}`} className={`absolute ${item.position} p-2 rounded-full bg-white dark:bg-gray-800 shadow-md cursor-pointer`} animate={{
                y: [0, -8, 0],
                rotate: [0, 10, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: index * 0.5
              }} onClick={scrollToEarlyAccess} data-id="element-2291">
                    {item.icon}
                  </motion.div>)}
              </div>
              <motion.h1 className="text-5xl sm:text-6xl font-bold mb-4 cursor-pointer" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.3
            }} onClick={scrollToEarlyAccess} data-id="element-2292">
                <span className="text-primary-600 dark:text-primary-400" data-id="element-2293">
                  Arabic
                </span>
                <span className="text-black dark:text-white" data-id="element-2294">With</span>
                <span className="text-blue-500" data-id="element-2295">English</span>
              </motion.h1>
              <motion.p className="text-2xl sm:text-3xl font-medium mb-4 bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-400 dark:to-blue-400 bg-clip-text text-transparent cursor-pointer" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.5
            }} onClick={scrollToEarlyAccess} data-id="element-2296">
                {isRTL ? 'تعلم الإنجليزية بسهولة... بالعربية' : 'Learn English Easily... in Arabic'}
              </motion.p>
              <motion.p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto cursor-pointer" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.7
            }} onClick={scrollToEarlyAccess} data-id="element-2297">
                {isRTL ? 'منصة تعليمية تشرح اللغة الإنجليزية بطريقة مبسطة ومباشرة باستخدام اللغة العربية، مصممة خصيصًا للمتحدثين بالعربية.' : 'An educational platform that explains English in a simplified and direct way using Arabic language, specially designed for Arabic speakers.'}
              </motion.p>
            </motion.div>
            {/* Interactive cards section - Enhanced */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-2298">
              {/* Word of the day card - Enhanced */}
              <motion.div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 cursor-pointer" initial={{
              opacity: 0,
              x: isRTL ? 50 : -50
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.7,
              delay: 0.2
            }} whileHover={{
              y: -5
            }} onClick={scrollToEarlyAccess} data-id="element-2299">
                <div className="flex justify-between items-center mb-4" data-id="element-2300">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100" data-id="element-2301">
                    {isRTL ? 'كلمة اليوم' : 'Word of the Day'}
                  </h3>
                  <div className="px-2 py-1 bg-primary-50 dark:bg-primary-900/30 rounded-md text-xs font-medium text-primary-600 dark:text-primary-400" data-id="element-2302">
                    {isRTL ? 'يومي' : 'Daily'}
                  </div>
                </div>
                <div className="flex flex-col items-center mb-6 relative" data-id="element-2303">
                  <motion.div className="text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2 flex items-center gap-3" whileHover={{
                  scale: 1.05
                }} animate={{
                  y: [0, -5, 0]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-2304">
                    {wordOfDay.english} <span className="text-red-500" data-id="element-2305">🍎</span>
                  </motion.div>
                  <div className="text-3xl font-bold text-blue-500 dark:text-blue-400 mb-1" data-id="element-2306">
                    {wordOfDay.arabic}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-3" data-id="element-2307">
                    {wordOfDay.pronunciation}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg mb-4" data-id="element-2308">
                  <p className="text-gray-700 dark:text-gray-300 text-center" data-id="element-2309">
                    <span className="font-medium" data-id="element-2310">
                      {isRTL ? 'الاستخدام:' : 'Usage:'}
                    </span>{' '}
                    {isRTL ? wordOfDay.usageAr : wordOfDay.usage}
                  </p>
                </div>
                <div className="flex justify-center" data-id="element-2311">
                  <motion.button className="flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors" whileTap={{
                  scale: 0.95
                }} whileHover={{
                  scale: 1.05
                }} onClick={scrollToEarlyAccess} data-id="element-2312">
                    <Volume2 className="w-5 h-5" data-id="element-2313" />
                    {isRTL ? 'استمع للنطق' : 'Listen to pronunciation'}
                  </motion.button>
                </div>
              </motion.div>
              {/* Countdown timer card - Enhanced */}
              <motion.div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 cursor-pointer" initial={{
              opacity: 0,
              x: isRTL ? -50 : 50
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.7,
              delay: 0.2
            }} whileHover={{
              y: -5
            }} onClick={scrollToEarlyAccess} data-id="element-2314">
                <div className="flex justify-between items-center mb-4" data-id="element-2315">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100" data-id="element-2316">
                    {isRTL ? 'الوقت المتبقي للإطلاق' : 'Time Until Launch'}
                  </h3>
                  <div className="px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-md text-xs font-medium text-blue-600 dark:text-blue-400" data-id="element-2317">
                    {isRTL ? 'قريبًا' : 'Coming Soon'}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-8" data-id="element-2318">
                  <div className="flex flex-col items-center" data-id="element-2319">
                    <motion.div className="w-20 h-20 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center text-4xl font-bold text-primary-600 dark:text-primary-400 shadow-inner" animate={{
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-2320">
                      05
                    </motion.div>
                    <span className="mt-2 text-gray-600 dark:text-gray-300 font-medium" data-id="element-2321">
                      {isRTL ? 'أيام' : 'Days'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-2322">
                    <motion.div className="w-20 h-20 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center text-4xl font-bold text-primary-600 dark:text-primary-400 shadow-inner" animate={{
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.3
                  }} data-id="element-2323">
                      01
                    </motion.div>
                    <span className="mt-2 text-gray-600 dark:text-gray-300 font-medium" data-id="element-2324">
                      {isRTL ? 'ساعات' : 'Hours'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-2325">
                    <motion.div className="w-20 h-20 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center text-4xl font-bold text-primary-600 dark:text-primary-400 shadow-inner" animate={{
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.6
                  }} data-id="element-2326">
                      33
                    </motion.div>
                    <span className="mt-2 text-gray-600 dark:text-gray-300 font-medium" data-id="element-2327">
                      {isRTL ? 'دقائق' : 'Min.'}
                    </span>
                  </div>
                </div>
                <motion.button className="w-full py-4 px-6 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20" whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.98
              }} onClick={scrollToEarlyAccess} data-id="element-2328">
                  <Book className="w-5 h-5" data-id="element-2329" />
                  {isRTL ? 'ابدأ تعلمك الآن' : 'Start Your Learning Now'}
                </motion.button>
              </motion.div>
            </div>
            {/* Benefits section - Enhanced */}
            <motion.div className="mb-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.3
          }} data-id="element-2330">
              {benefits.map((benefit, index) => <motion.div key={index} className="bg-white/80 dark:bg-dark-200/50 backdrop-blur-sm rounded-xl p-6 border border-gray-200/50 dark:border-gray-700/30 hover:border-primary-200 dark:hover:border-primary-700/50 transition-all duration-300 cursor-pointer" whileHover={{
              y: -5,
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
            }} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4 + index * 0.1
            }} onClick={scrollToEarlyAccess} data-id="element-2331">
                  <div className="flex items-start gap-4" data-id="element-2332">
                    <motion.div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30" animate={{
                  rotate: [0, 10, 0, -10, 0]
                }} transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: index * 0.2
                }} data-id="element-2333">
                      {benefit.icon}
                    </motion.div>
                    <div data-id="element-2334">
                      <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white" data-id="element-2335">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2336">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </motion.div>
            {/* Learning path section - Enhanced */}
            <motion.div className="mb-20" initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.4
          }} data-id="element-2337">
              <h2 className="text-3xl font-bold text-center mb-10 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-400 dark:to-blue-400 cursor-pointer" onClick={scrollToEarlyAccess} data-id="element-2338">
                {isRTL ? 'رحلة التعلم مع Arabic With English' : 'Learning Journey with Arabic With English'}
              </h2>
              <div className="relative" data-id="element-2339">
                <div className="absolute left-0 right-0 top-1/2 h-2 bg-gray-200 dark:bg-gray-700 -translate-y-1/2 rounded-full z-0" data-id="element-2340"></div>
                <motion.div className="absolute left-0 right-0 top-1/2 h-2 bg-gradient-to-r from-blue-500 to-primary-500 -translate-y-1/2 rounded-full z-0" style={{
                width: '75%'
              }} initial={{
                width: '0%'
              }} animate={{
                width: '75%'
              }} transition={{
                duration: 1.5,
                delay: 0.5
              }} data-id="element-2341"></motion.div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10" data-id="element-2342">
                  {learningSteps.map((step, index) => <motion.div key={step.id} className="flex flex-col items-center cursor-pointer" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.5,
                  delay: 0.5 + index * 0.1
                }} onClick={scrollToEarlyAccess} data-id="element-2343">
                      <motion.div className="w-16 h-16 rounded-full bg-white dark:bg-gray-800 border-4 border-gray-100 dark:border-gray-700 flex items-center justify-center shadow-lg mb-4" whileHover={{
                    scale: 1.1,
                    y: -5
                  }} animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: index * 0.3
                  }} data-id="element-2344">
                        {step.icon}
                      </motion.div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1" data-id="element-2345">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 text-center" data-id="element-2346">
                        {step.example}
                      </p>
                    </motion.div>)}
                </div>
              </div>
            </motion.div>
            {/* CTA button - Enhanced */}
            <motion.div className="text-center mb-12" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.7,
            delay: 0.6
          }} data-id="element-2347">
              <motion.button className="py-4 px-8 bg-primary-600 hover:bg-primary-700 text-white rounded-full font-medium text-lg shadow-xl shadow-primary-500/20 flex items-center gap-2 mx-auto" whileHover={{
              scale: 1.05,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }} whileTap={{
              scale: 0.98
            }} onClick={scrollToEarlyAccess} data-id="element-2348">
                <Zap className="w-5 h-5" data-id="element-2349" />
                {isRTL ? 'سجل الآن واحصل على خصم 50%' : 'Register Now & Get 50% Discount'}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} data-id="element-2350" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Core Language Skills Section */}
      <LanguageSkillsSection data-id="element-2351" />
      {/* Learning Progress Section - NEW */}
      <LearningProgressSection data-id="element-2352" />
      {/* Study Plan Section - NEW */}
      <StudyPlanSection data-id="element-2353" />
      {/* Learning Activities Section */}
      <LearningActivitiesSection data-id="element-2354" />
      {/* Features section */}
      <WhySection features={features} data-id="element-2355" />
      {/* CTA section */}
      <CTA data-id="element-2356" />
    </div>;
};
// Missing components for icons
function X(props) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-2357">
      <path d="M18 6 6 18" data-id="element-2358" />
      <path d="m6 6 12 12" data-id="element-2359" />
    </svg>;
}
function Home(props) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-2360">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" data-id="element-2361" />
      <polyline points="9 22 9 12 15 12 15 22" data-id="element-2362" />
    </svg>;
}
function HelpCircle(props) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-2363">
      <circle cx="12" cy="12" r="10" data-id="element-2364" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" data-id="element-2365" />
      <path d="M12 17h.01" data-id="element-2366" />
    </svg>;
}
function Clock(props) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-2367">
      <circle cx="12" cy="12" r="10" data-id="element-2368" />
      <polyline points="12 6 12 12 16 14" data-id="element-2369" />
    </svg>;
}